import { viewTechDocRouteRef } from '@agilelab/plugin-wb-builder-catalog';
import { WitboostTemplate } from '@agilelab/plugin-wb-builder-common';
import {
  AutoFixHighIcon,
  FormatListBulletedOffIcon,
  Severity,
  SeverityTooltip,
  SystemIcon,
  WbFavoriteEntityButton,
  WbTable,
  WbTagsArray,
  WbTruncatedTypographyWrapper,
} from '@agilelab/plugin-wb-platform';
import {
  DEFAULT_NAMESPACE,
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  ScmIntegrationIcon,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import { catalogLocationCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { getEntitySourceLocation } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  Box,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import FormatListBulleted from '@material-ui/icons/FormatListBulleted';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { FC, MouseEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { selectedTemplateRouteRef } from '../../routes';
import { useScaffolderPageContext } from './context/useScaffolderPageContext';
import {
  CustomTemplate,
  findDuplicatesCustomTemplates,
  useWitboostCustomTemplates,
} from './utils';

const useStyles = makeStyles((theme: Theme) => ({
  avatar: {
    minWidth: '24px',
    width: '24px',
    maxWidth: '24px',
    height: '24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1.125rem',
    pointerEvents: 'none',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
    padding: '2px',
    borderRadius: '8px',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
}));

const NameColumn = (entity: Entity) => {
  const classes = useStyles();
  const getInitials = (title: string) =>
    title
      .split(' ')
      .slice(0, 2)
      .map(s => s.charAt(0));

  const icon: string | undefined = (entity.metadata?.mesh as any)?.icon;
  const title = entity.metadata.title;

  const renderIcon = () => {
    if (entity.spec?.type === 'dataproduct') {
      return (
        <Box className={classes.avatar}>
          <SystemIcon />
        </Box>
      );
    }
    return (
      <Box
        className={classes.avatar}
        style={{
          backgroundImage: icon ? `url(${icon})` : '',
        }}
      >
        {!icon && title && (
          <Typography color="primary">{getInitials(title)}</Typography>
        )}
      </Box>
    );
  };

  return (
    <Box style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
      <WbFavoriteEntityButton entity={entity} />
      {renderIcon()}
      <WbTruncatedTypographyWrapper
        value={title ?? ''}
        style={{ fontWeight: 700 }}
      />
    </Box>
  );
};

const TypeColumn = (entity: Entity) => {
  return (
    <WbTruncatedTypographyWrapper
      value={
        (entity?.metadata.classDetails as Record<string, any>).displayName ??
        'Others'
      }
    />
  );
};

const DescriptionColumn = (entity: Entity) => {
  return (
    <WbTruncatedTypographyWrapper value={entity.metadata.description || ''} />
  );
};

const TagsColumn = (entity: Entity) => {
  return (
    <WbTagsArray
      tags={(entity.metadata.tags || []).map(t => ({ tagFQN: t }))}
    />
  );
};

const ActionColumn = ({
  witboostCustomTemplates,
  entity,
}: {
  witboostCustomTemplates: Record<string, CustomTemplate[]>;
  entity: Entity;
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // CODE LINK
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const sourceLocation = getEntitySourceLocation(entity, scmIntegrationsApi);

  // TECH DOCS LINK
  const viewTechDoc = useRouteRef(viewTechDocRouteRef);
  const viewTechDocsAnnotation =
    entity.metadata.annotations?.['backstage.io/techdocs-ref'];
  const viewTechDocsLink =
    !!viewTechDocsAnnotation &&
    !!viewTechDoc &&
    viewTechDoc({
      namespace: entity.metadata.namespace ?? DEFAULT_NAMESPACE,
      kind: entity.kind,
      name: entity.metadata.name,
    });

  // REVERSE PROVISIONING LINK
  const customTemplates =
    witboostCustomTemplates[
      (
        entity as WitboostTemplate
      ).spec.steps[0].input.values.useCaseTemplateId?.toLocaleLowerCase()
    ];
  const reverseProvisioningTemplateUrl = customTemplates?.find(
    customTemplate => customTemplate.kind === 'reverseprovisioningtemplate',
  );

  // EDIT TEMPLATE LINK
  const editTemplateUrl = findDuplicatesCustomTemplates(
    'edittemplate',
    customTemplates,
  );

  const hasNoActions = Boolean(
    !sourceLocation &&
      !editTemplateUrl.result &&
      !reverseProvisioningTemplateUrl &&
      !viewTechDocsLink,
  );

  if (hasNoActions) return null;

  return (
    <div>
      <IconButton
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label={open ? 'close' : 'more'}
        onClick={handleClick}
        style={{
          width: '32px',
          height: '32px',
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={e => {
          e.stopPropagation();
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {sourceLocation && (
          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              window.location.href = sourceLocation.locationTargetUrl;
            }}
          >
            <ScmIntegrationIcon type={sourceLocation.integrationType} />
            Open Repository
          </MenuItem>
        )}
        {editTemplateUrl.result && editTemplateUrl.result.ref && (
          <MenuItem
            onClick={() => {
              window.location.href = editTemplateUrl.result!.ref;
            }}
            className={classes.menuItem}
          >
            <FormatListBulleted fontSize="small" />
            Editor Wizard
          </MenuItem>
        )}
        {reverseProvisioningTemplateUrl && (
          <MenuItem
            onClick={() => {
              window.location.href = reverseProvisioningTemplateUrl.ref;
            }}
            className={classes.menuItem}
          >
            <AutoFixHighIcon />
            Reverse Provisioning
          </MenuItem>
        )}
        {viewTechDocsLink && (
          <MenuItem
            onClick={() => {
              navigate(viewTechDocsLink);
            }}
            style={{ borderTop: `1px solid ${theme.palette.grey[300]}` }}
            className={classes.menuItem}
          >
            <InfoIcon fontSize="small" />
            Documentation
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

const ErrorColumn = ({
  witboostCustomTemplates,
  entity,
}: {
  witboostCustomTemplates: Record<string, CustomTemplate[]>;
  entity: Entity;
}) => {
  const { errors } = useScaffolderPageContext();

  const customTemplates =
    witboostCustomTemplates[
      (
        entity as WitboostTemplate
      ).spec.steps[0].input.values.useCaseTemplateId?.toLocaleLowerCase()
    ];

  const editTemplateUrl = findDuplicatesCustomTemplates(
    'edittemplate',
    customTemplates,
  );

  const error = errors?.find(e => e.entity_id === entity.metadata.uid);

  return (
    <>
      {error?.errorMessage && (
        <SeverityTooltip severity={Severity.Error} label={error.errorMessage} />
      )}
      {editTemplateUrl.error && (
        <SeverityTooltip
          severity={Severity.Error}
          label={editTemplateUrl.errorMessage ?? 'error'}
          icon={<FormatListBulletedOffIcon />}
          to={`/catalog/${
            entity?.metadata.namespace ?? 'default'
          }/${entity?.kind?.toLocaleLowerCase()}/${entity?.metadata.name}`}
        />
      )}
    </>
  );
};

export const ScaffolderTable: FC = () => {
  const navigate = useNavigate();
  const { entities, loading } = useScaffolderPageContext();
  const witboostCustomTemplates = useWitboostCustomTemplates(entities);
  const location = useLocation();
  const knownSystem = location.state?.system;
  const knownDomain = location.state?.domain;

  const { allowed } = usePermission({
    permission: catalogLocationCreatePermission,
  });

  const templateRoute = useRouteRef(selectedTemplateRouteRef);

  return (
    <WbTable<any>
      components={{
        tableLoader: { loading },
        tableContent: {
          columns: [
            {
              field: 'metadata.title',
              headerName: 'Name',
              width: 300,
              fieldRender: NameColumn,
              sortable: true,
            },
            {
              field: 'metadata.classDetails.displayName',
              headerName: 'Type',
              fieldRender: TypeColumn,
              sortable: true,
              width: 150,
            },
            {
              field: 'metadata.description',
              headerName: 'Description',
              fieldRender: DescriptionColumn,
              sortable: true,
            },
            {
              field: 'metadata.tags',
              headerName: 'Tags',
              fieldRender: TagsColumn,
            },
            {
              field: 'actions',
              headerName: '',
              width: 100,
              minWidth: 100,
              maxWidth: 100,
              resizable: false,
              fieldRender: e => (
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <ErrorColumn
                    entity={e}
                    witboostCustomTemplates={witboostCustomTemplates}
                  />
                  <ActionColumn
                    entity={e}
                    witboostCustomTemplates={witboostCustomTemplates}
                  />
                </div>
              ),
            },
          ],
          rows: entities,
          onRowClick: (entity, e) => {
            e.stopPropagation();
            e.preventDefault();
            const { name, namespace } = parseEntityRef(
              stringifyEntityRef(entity),
            );
            const href = templateRoute({ templateName: name, namespace });

            if (allowed) {
              const to =
                knownDomain && knownSystem
                  ? `${href}?formData=%7B"domain"%3A"${knownDomain}"%2C"dataproduct"%3A"system:${knownSystem}"%7D`
                  : href;

              navigate(to);
            }
          },
        },
      }}
    />
  );
};
